import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusPill } from "@/components/shared/StatusPill";
import { Loading } from "@/components/shared/Loading";
import { DocumentPanel } from "@/components/shared/DocumentPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AttestDialog } from "@/components/finance/AttestDialog";
import { ExceptionDialog } from "@/components/finance/ExceptionDialog";
import { FinanceCustomerContext } from "@/components/finance/FinanceCustomerContext";
import { HandoffConfirmDialog } from "@/components/shared/HandoffConfirmDialog";
import { useFileSelectConfirm, FileSelectConfirmBar } from "@/components/shared/FileSelectConfirm";
import { api } from "@/api/client";
import { money } from "@/lib/format";
import { apiErrorMessage, isConflict, isVersionConflict } from "@/lib/apiError";
import { useApp } from "@/context/AppContext";
import {
  useRoleQuery, useFinancialProfile, useFinanceDecision, useExposureSnapshots,
  useFinanceHolds, useAdvanceRequests, useCustomerDocuments,
} from "@/api/hooks";
import {
  ArrowLeft, ShieldCheck, ShieldAlert, AlertTriangle, RefreshCw, Loader2, ChevronDown, ChevronUp,
  Phone, MessageCircle, Mail, Upload, FileCheck, Gavel, PackageCheck, Check, Lock, Clock,
} from "lucide-react";

const useOrderSummary = (aid) => useRoleQuery(["order-summary", aid], `/finance-gate/acceptances/${aid}/order-summary`, { enabled: !!aid });

const HOLD_ACTIONS = {
  ADVANCE_NOT_RECEIVED: { label: "Request payment", kind: "advance" },
  PAYMENT_PROOF_UNVERIFIED: { label: "Go to advance verification", kind: "advance" },
  CREDIT_LIMIT_EXCEEDED: { label: "Request Management exception", kind: "exception", decisionType: "FINANCE_CREDIT_EXCEPTION" },
  OVERDUE_BALANCE: { label: "Request overdue exception", kind: "exception", decisionType: "FINANCE_OVERDUE_EXCEPTION" },
  FINANCIAL_DATA_INCOMPLETE: { label: "Refresh financial data", kind: "attest" },
  ACCOUNT_BLOCKED: { label: "Review account status", kind: "profile" },
  SECURITY_REQUIRED: { label: "Confirm security received, then release", kind: "release_hold" },
  KYC_FINANCE_BLOCK: { label: "Confirm KYC cleared, then release", kind: "release_hold" },
  MANAGEMENT_APPROVAL_PENDING: { label: "Check Approvals", kind: "approvals" },
  OTHER: { label: "Release hold", kind: "release_hold" },
};

export default function FinanceOrderReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { openComposer } = useApp();
  const [showExposureDetail, setShowExposureDetail] = useState(false);
  const [attestOpen, setAttestOpen] = useState(false);
  const [exceptionCfg, setExceptionCfg] = useState(null);
  const [verifyAdv, setVerifyAdv] = useState(null);
  const [proofAdv, setProofAdv] = useState(null);
  const [docOpen, setDocOpen] = useState(false);
  const [activeDoc, setActiveDoc] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const [creatingJob, setCreatingJob] = useState(false);
  const [handoffOpen, setHandoffOpen] = useState(false);

  const { data: summary, refetch: refetchSummary } = useOrderSummary(id);
  const clientId = summary?.customer?.id;
  // Contact-the-customer actions — same Composer/openComposer capability
  // already used elsewhere on this page (the per-advance "Request payment"
  // button), just also surfaced as general Call/WhatsApp/Email actions in
  // the header, matching the pattern Sales' Customer360 already has. The
  // backend resolves the real recipient address from customerId server-side
  // (see _select_outbound_recipient), so no separate contact list is needed
  // here.
  const comms = (ch) => openComposer({ channel: ch, customerId: clientId, intent: `respond to ${summary?.customer?.name || "the customer"} about ${id}`, subjectHint: `[${id}] ` });
  const { data: profile } = useFinancialProfile(clientId);
  const { data: decision } = useFinanceDecision(id);
  const { data: snapshots } = useExposureSnapshots(id);
  const { data: holds } = useFinanceHolds(id);
  const { data: advances } = useAdvanceRequests(id);
  const { data: custDocs } = useCustomerDocuments(clientId);

  if (!summary) return <Loading />;

  const acc = summary.acceptance || {};
  const gateStatus = acc.financeGateStatus;
  const openHolds = (holds || []).filter((h) => h.status === "OPEN");
  const latestSnapshot = (snapshots || [])[0];
  const attestFresh = (field) => {
    const asOf = profile?.[`${field}_asOf`];
    // Bug fix: an attested value of exactly 0 (e.g. "Overdue AR: 0") is a
    // real, valid attestation — `!profile[field]` treated it as falsy and
    // permanently flagged it "Needs Refresh" even seconds after attesting.
    if (profile?.[field] == null || !asOf) return null;
    const days = (Date.now() - new Date(asOf).getTime()) / 86400000;
    return days <= 7;
  };
  const attestedFields = ["knownOutstandingAR", "knownOverdueAR", "knownOpenJobExposure", "knownUnbilledExposure"];
  const anyStale = attestedFields.some((f) => attestFresh(f) === false || attestFresh(f) === null);

  const refreshAll = () => {
    ["order-summary", "fin-profile", "fin-decision", "fin-exposure", "fin-holds", "fin-advances"].forEach((k) =>
      qc.invalidateQueries({ queryKey: [k] }));
  };

  // Bug fix: this page's `summary` (and therefore `acc.version`) can go
  // stale relative to the DB — the acceptance record may have been touched
  // by another action (or simply sat open across a while) since it was
  // fetched. Evaluating with a stale version always produced a real
  // CONCURRENT_MODIFICATION error ("This order changed since you loaded
  // it — refreshing"), and the user had to notice the toast and click the
  // button a second time for it to actually take effect — indistinguishable
  // from "the button doesn't work". Fetching the current version
  // immediately before acting, instead of trusting whatever was already in
  // memory, makes the first click reliable.
  // Bug fix (Aug 2026) — a version conflict on the very first click was
  // common (this page's data can go stale the instant it loads) and forced
  // the user to notice a purely technical "changed since you loaded it"
  // message and click a second time to see anything real. Now retries once
  // automatically with the freshly-refetched version, silently — the user
  // should only ever see a message about the actual evaluation outcome.
  // That outcome message also now always states the real reason the gate
  // decision moved (or didn't) — e.g. which document/attestation/advance is
  // actually missing — instead of a generic "re-evaluated" toast that told
  // Finance nothing about what to do next.
  const evaluateGateOnce = (expectedVersion) => api.post(`/finance-gate/acceptances/${id}/evaluate`, { expectedVersion });

  const runGate = async () => {
    setEvaluating(true);
    try {
      let fresh = await refetchSummary();
      let expectedVersion = fresh.data?.acceptance?.version ?? acc.version;
      let data;
      try {
        ({ data } = await evaluateGateOnce(expectedVersion));
      } catch (e) {
        // Only retry for a genuine stale-`version` race — a plain HTTP 409
        // can also mean a real business rule was rejected (e.g. the
        // quotation was revised and needs a fresh acceptance), and retrying
        // that just reproduces the same rejection while hiding the real
        // reason. Those should surface immediately below instead.
        if (!isVersionConflict(e)) throw e;
        fresh = await refetchSummary();
        expectedVersion = fresh.data?.acceptance?.version ?? acc.version;
        ({ data } = await evaluateGateOnce(expectedVersion));
      }
      refreshAll();
      const outcome = data?.decision?.decision;
      const reason = data?.decision?.reason;
      if (outcome === "RELEASED") toast.success("Finance Gate: Released — ready to hand off to Operations");
      else toast.info(`Finance Gate: ${outcome || "evaluated"}${reason ? ` — ${reason}` : ""}`, { duration: 8000 });
    } catch (e) {
      if (isVersionConflict(e)) { toast.error("This order is being updated elsewhere right now — please try again in a moment."); refreshAll(); }
      else { toast.error(apiErrorMessage(e, "Could not evaluate — please refresh and retry"), { duration: 8000 }); refreshAll(); }
    } finally { setEvaluating(false); }
  };

  const createJob = async () => {
    setCreatingJob(true);
    try {
      // Freshness rule (Part 24): always re-run the gate immediately before
      // a consequential action instead of trusting whatever was on screen.
      // Same stale-version fix as runGate() above — fetch the current
      // version first rather than trusting the in-memory copy.
      const fresh = await refetchSummary();
      const expectedVersion = fresh.data?.acceptance?.version ?? acc.version;
      const { data } = await api.post(`/finance-gate/acceptances/${id}/create-job`, { expectedVersion });
      toast.success(`${data.job.id} created — ready for Operations review`);
      setHandoffOpen(false);
      navigate("/finance/credit");
    } catch (e) {
      if (isConflict(e)) {
        toast.error("This order's Finance state changed — refreshing, please try again.");
        refreshAll();
      } else {
        toast.error(apiErrorMessage(e, "Could not create Job"));
      }
    } finally { setCreatingJob(false); }
  };

  const releaseHold = async (holdId, releaseReason) => {
    try {
      await api.post(`/finance-gate/holds/${holdId}/release`, { releaseReason });
      toast.success("Finance Hold released");
      refreshAll();
    } catch (e) { toast.error(apiErrorMessage(e, "Could not release hold")); }
  };

  const findProofDoc = (docId) => (custDocs?.documents || []).find((d) => d.id === docId);

  return (
    <>
      <PageHeader
        title={<span className="flex items-center gap-2"><button onClick={() => navigate("/finance/credit")} className="text-slate-400 hover:text-slate-700"><ArrowLeft className="h-4 w-4" /></button>{summary.customer?.name}</span>}
        subtitle={`${summary.quotationId} V${summary.quotationVersion} · Client ${clientId}`}
        actions={<div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-8" onClick={() => comms("call")} data-testid="fg-call"><Phone className="h-3.5 w-3.5 mr-1.5" />Call</Button>
          <Button size="sm" variant="outline" className="h-8" onClick={() => comms("whatsapp")} data-testid="fg-whatsapp"><MessageCircle className="h-3.5 w-3.5 mr-1.5" />WhatsApp</Button>
          <Button size="sm" variant="outline" className="h-8" onClick={() => comms("email")} data-testid="fg-email"><Mail className="h-3.5 w-3.5 mr-1.5" />Email</Button>
          {gateStatus && <StatusPill status={gateStatus} />}
          <Button size="sm" variant="outline" className="h-8" onClick={runGate} disabled={evaluating} data-testid="fg-evaluate">
            {evaluating ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Run Finance Gate check
          </Button>
          {/* Aug 2026 IA correction — item 3, Finance side: the one handoff
              action for this order, persistently visible top-right once
              Finance Gate has RELEASED it, rather than only reachable by
              scrolling into the verdict card below. Same real endpoint as
              before (POST .../create-job) — just relocated and now behind
              the shared confirmation dialog. */}
          {gateStatus === "RELEASED" ? (
            <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 ml-1" onClick={() => setHandoffOpen(true)} disabled={creatingJob} data-testid="fg-create-job-header">
              <PackageCheck className="h-3.5 w-3.5 mr-1.5" />Create Job — Hand off to Operations
            </Button>
          ) : (
            <span className="ml-1 text-[11px] text-slate-400 italic max-w-[240px] text-right" data-testid="fg-handoff-unavailable">
              Available once the Finance Gate verdict is Released
            </span>
          )}
        </div>} />

      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {/* Order summary — no unnecessary Sales CRM detail */}
        <Card title="Order">
          <Grid>
            <Stat label="Accepted amount" value={money(summary.acceptedAmount, summary.currency)} accent />
            <Stat label="Service / Route" value={`${summary.scope?.mode || "—"} ${summary.scope?.direction || ""}`} />
            <Stat label="Origin → Destination" value={`${summary.scope?.origin || "—"} → ${summary.scope?.destination || "—"}`} />
            <Stat label="Commercial scope" value={summary.scope?.scope || "—"} />
            <Stat label="Payment terms" value={summary.paymentTerms || "—"} />
            <Stat label="Incoterm" value={summary.scope?.incoterm || "—"} />
            <Stat label="Acceptance evidence" value={`${summary.evidence?.evidenceType || "—"} · ${summary.evidence?.source || "—"}`} />
            <Stat label="Verified by" value={`${summary.evidence?.verifiedBy || "—"}`} />
          </Grid>
        </Card>

        {/* Complete customer information + all submitted documents, so
            Finance never has to leave this workspace to verify who they're
            making a credit decision about. Shared with the Invoice
            Clearance case detail in Pipeline.jsx — same component, same
            data, not duplicated. */}
        <FinanceCustomerContext clientId={clientId} />

        {/* Finance profile */}
        <Card title="Finance profile" action={<Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAttestOpen(true)} data-testid="fg-attest-open">Attest / update financial data</Button>}>
          {!profile ? <Loading /> : (
            <>
              {profile.attestedBy && profile.attestedAt && (
                <div className="mb-3 flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="fg-attested-banner">
                  <div className="flex items-center gap-2 text-emerald-800 text-[12px] font-semibold">
                    <ShieldCheck className="h-3.5 w-3.5" /> ATTESTED
                  </div>
                  <div className="text-[11px] text-emerald-700">by {profile.attestedBy} · {new Date(profile.attestedAt).toLocaleString()}</div>
                </div>
              )}
              <Grid>
                <Stat label="Account type" value={profile.accountType} />
                <Stat label="Credit status" value={profile.financialStatus} />
                <Stat label="Existing / approved credit limit" value={profile.approvedCreditLimit != null ? money(profile.approvedCreditLimit) : "Not set"} />
                <Stat label="Payment terms" value={profile.paymentTermsDays != null ? `${profile.paymentTermsDays} days` : "—"} />
                <Stat label="Requested credit limit" value={profile.requestedCreditLimit != null ? money(profile.requestedCreditLimit) : "—"} />
                <Stat label="Recommended credit limit" value={profile.recommendedCreditLimit != null ? money(profile.recommendedCreditLimit) : "—"} />
                <Stat label="Current outstanding" value={profile.knownOutstandingAR != null ? money(profile.knownOutstandingAR) : "—"} />
                <Stat label="Available credit"
                  value={profile.approvedCreditLimit != null && profile.knownOutstandingAR != null
                    ? money(profile.approvedCreditLimit - profile.knownOutstandingAR) : "—"} />
                <Stat label="Annual turnover" value={profile.annualTurnover != null ? money(profile.annualTurnover) : "—"} />
                <Stat label="Average monthly revenue" value={profile.averageMonthlyRevenue != null ? money(profile.averageMonthlyRevenue) : "—"} />
                <Stat label="Previous trade count" value={profile.previousTradeCount != null ? profile.previousTradeCount : "—"} />
                <Stat label="Total historical business" value={profile.totalHistoricalBusiness != null ? money(profile.totalHistoricalBusiness) : "—"} />
                <Stat label="Previous payment behaviour" value={profile.previousPaymentBehaviour || "—"} />
                <Stat label="Credit risk" value={profile.creditRisk || "—"} danger={profile.creditRisk === "HIGH"} />
                <Stat label="Last financial review" value={profile.lastReviewedAt ? new Date(profile.lastReviewedAt).toLocaleDateString() : "—"} />
                <Stat label="Reviewed by" value={profile.lastReviewedBy || "—"} />
              </Grid>
              {profile.financeRecommendation && (
                <div className="mt-3 text-[12px] text-slate-600"><span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Finance recommendation: </span>{profile.financeRecommendation}</div>
              )}
              {profile.notes && (
                <div className="mt-1 text-[12px] text-slate-600"><span className="font-semibold text-slate-500 uppercase tracking-wider text-[10px]">Notes: </span>{profile.notes}</div>
              )}
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Finance-attested inputs (7-day freshness window)</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {attestedFields.map((f) => {
                    const fresh = attestFresh(f);
                    return (
                      <div key={f} className="rounded-lg border border-slate-200 p-2.5">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{f.replace("known", "").replace(/([A-Z])/g, " $1").trim()}</div>
                        <div className="text-sm font-mono-num font-semibold text-slate-800 mt-0.5">{profile[f] != null ? money(profile[f]) : "—"}</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">by {profile[`${f}_by`] || "—"} · {profile[`${f}_asOf`] ? new Date(profile[`${f}_asOf`]).toLocaleString() : "never"}</div>
                        <StatusPill className="mt-1" status={fresh === true ? "Current" : fresh === false || fresh === null ? "Needs Refresh" : "Needs Refresh"} />
                      </div>
                    );
                  })}
                </div>
                {anyStale && (
                  <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="fg-stale-banner">
                    <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
                    <div>Financial data refresh required. Until every attested figure above is current, the Finance Gate cannot reach RELEASED for this client.</div>
                  </div>
                )}
              </div>
            </>
          )}
        </Card>

        {/* Exposure */}
        <Card title="Exposure">
          {!latestSnapshot ? (
            <div className="text-sm text-slate-500">Run the Finance Gate check to calculate exposure for this order.</div>
          ) : latestSnapshot.status !== "COMPLETE" ? (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">Exposure could not be fully calculated</div>
                <ul className="list-disc ml-4 mt-1 space-y-0.5">{(latestSnapshot.missingComponents || []).map((m, i) => <li key={i}>{m}</li>)}</ul>
              </div>
            </div>
          ) : (
            <>
              <Grid>
                <Stat label="Approved credit limit" value={latestSnapshot.creditLimit != null ? money(latestSnapshot.creditLimit) : "Cash account"} />
                <Stat label="Current exposure" value={money(latestSnapshot.totalExposureBefore)} />
                <Stat label="Current order" value={money(latestSnapshot.currentOrderExposure, latestSnapshot.currency)} accent />
                <Stat label="Exposure after order" value={money(latestSnapshot.totalExposureAfter)} danger={latestSnapshot.creditLimit != null && latestSnapshot.totalExposureAfter > latestSnapshot.creditLimit} />
                <Stat label="Available credit after" value={latestSnapshot.availableCreditAfter != null ? money(latestSnapshot.availableCreditAfter) : "—"} danger={latestSnapshot.availableCreditAfter != null && latestSnapshot.availableCreditAfter < 0} />
                <Stat label="Overdue" value={latestSnapshot.overdueAR ? money(latestSnapshot.overdueAR) : "None"} danger={latestSnapshot.overdueAR > 0} />
              </Grid>
              <button className="mt-3 text-[12px] font-medium text-indigo-600 flex items-center gap-1" onClick={() => setShowExposureDetail((v) => !v)} data-testid="fg-exposure-detail-toggle">
                {showExposureDetail ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />} Calculation detail
              </button>
              {showExposureDetail && (
                <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-3 text-[12px] text-slate-600 bg-slate-50 rounded-lg p-3">
                  <div>Outstanding AR: <b>{money(latestSnapshot.outstandingAR || 0)}</b></div>
                  <div>Open job exposure: <b>{money(latestSnapshot.openJobExposure || 0)}</b></div>
                  <div>Unbilled exposure: <b>{money(latestSnapshot.unbilledExposure || 0)}</b></div>
                  <div>Pending order exposure: <b>{money(latestSnapshot.pendingOrderExposure || 0)}</b></div>
                  <div>Snapshot: <span className="font-mono-num">{latestSnapshot.id}</span></div>
                  <div>Calculated: {new Date(latestSnapshot.calculatedAt).toLocaleString()}</div>
                </div>
              )}
            </>
          )}
        </Card>

        {/* Gate verdict */}
        <GateVerdict decision={decision} gateStatus={gateStatus} holds={openHolds} advances={advances}
          onException={(cfg) => setExceptionCfg(cfg)} onCreateJob={createJob} creatingJob={creatingJob} />

        {/* Advances */}
        {(advances || []).length > 0 && (
          <Card title="Advance requests">
            <div className="space-y-3">
              {advances.map((a) => (
                <AdvanceRow key={a.id} adv={a} onCommunicate={() => openComposer({
                  channel: "whatsapp", parties: [summary.customer?.name], defaultParty: summary.customer?.name,
                  customerId: clientId, advanceRequestId: a.id,
                  subjectHint: `Payment request — ${a.purpose}`,
                  intent: `politely request payment of ${a.currency} ${a.requestedAmount} for ${a.purpose}, due ${a.dueAt || "as soon as possible"}. Do not mention internal credit limits or exposure calculations.`,
                })}
                  onAttachProof={() => setProofAdv(a)} onVerify={() => setVerifyAdv(a)}
                  onViewProof={() => { const d = findProofDoc(a.paymentProofDocumentId); if (d) { setActiveDoc(d); setDocOpen(true); } else toast.error("Proof document not found in customer documents yet"); }}
                  onWaive={() => setExceptionCfg({ decisionType: "FINANCE_ADVANCE_SHORTFALL_WAIVER", objectType: "advance_request", objectId: a.id, defaultAmount: a.shortfall, contextSummary: `Advance ${a.id} · shortfall ${money(a.shortfall, a.currency)}` })} />
              ))}
            </div>
          </Card>
        )}

        {/* Finance Holds */}
        {openHolds.length > 0 && (
          <Card title="Active Finance Holds">
            <div className="space-y-3">
              {openHolds.map((h) => {
                const cfg = HOLD_ACTIONS[h.reason] || HOLD_ACTIONS.OTHER;
                return (
                  <div key={h.id} className="rounded-lg border border-red-200 bg-red-50 p-3.5" data-testid={`hold-${h.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-red-800 text-sm">{h.reason.replace(/_/g, " ")}</div>
                      <StatusPill status="ON_HOLD" />
                    </div>
                    <div className="text-[13px] text-red-700 mt-1">{h.description}</div>
                    {h.amount != null && <div className="text-[12px] text-red-600 mt-0.5">Financial impact: {money(h.amount)}</div>}
                    <div className="text-[11px] text-slate-500 mt-1">Placed by {h.createdBy} · {new Date(h.createdAt).toLocaleString()}</div>
                    {h.releaseCondition && <div className="text-[11px] text-slate-500">Release condition: {h.releaseCondition}</div>}
                    <div className="mt-2">
                      {cfg.kind === "attest" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAttestOpen(true)} data-testid={`hold-action-${h.id}`}>{cfg.label}</Button>}
                      {cfg.kind === "exception" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setExceptionCfg({ decisionType: cfg.decisionType, objectType: "customer_acceptance", objectId: id, defaultAmount: h.amount, contextSummary: h.description })} data-testid={`hold-action-${h.id}`}>{cfg.label}</Button>}
                      {cfg.kind === "advance" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => document.getElementById("advance-section")?.scrollIntoView({ behavior: "smooth" })} data-testid={`hold-action-${h.id}`}>{cfg.label}</Button>}
                      {cfg.kind === "release_hold" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => releaseHold(h.id, `${cfg.label} — confirmed by Finance`)} data-testid={`hold-action-${h.id}`}>{cfg.label}</Button>}
                      {cfg.kind === "approvals" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate("/finance/approvals")} data-testid={`hold-action-${h.id}`}>{cfg.label}</Button>}
                      {cfg.kind === "profile" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAttestOpen(true)} data-testid={`hold-action-${h.id}`}>{cfg.label}</Button>}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>

      <AttestDialog open={attestOpen} onOpenChange={setAttestOpen} clientId={clientId} profile={profile} onDone={refreshAll} />
      {exceptionCfg && (
        <ExceptionDialog open={!!exceptionCfg} onOpenChange={(v) => !v && setExceptionCfg(null)} acceptanceId={id}
          decisionType={exceptionCfg.decisionType} objectType={exceptionCfg.objectType} objectId={exceptionCfg.objectId}
          defaultAmount={exceptionCfg.defaultAmount} contextSummary={exceptionCfg.contextSummary} onDone={refreshAll} />
      )}
      <VerifyAdvanceDialog adv={verifyAdv} onOpenChange={(v) => !v && setVerifyAdv(null)} onDone={refreshAll} />
      <AttachProofDialog adv={proofAdv} customerId={clientId} onOpenChange={(v) => !v && setProofAdv(null)} onDone={refreshAll} />
      <DocumentPanel doc={activeDoc} open={docOpen} onOpenChange={setDocOpen} />
      <HandoffConfirmDialog
        open={handoffOpen}
        onOpenChange={setHandoffOpen}
        busy={creatingJob}
        title="Create Job — Hand off to Operations"
        confirmLabel="Confirm and create Job"
        summaryRows={[
          { label: "Customer", value: summary.customer?.name },
          { label: "Quotation", value: `${summary.quotationId} V${summary.quotationVersion}` },
          { label: "Accepted amount", value: money(summary.acceptedAmount, summary.currency) },
          { label: "Payment terms", value: summary.paymentTerms || "—" },
          { label: "Finance Gate verdict", value: "RELEASED" },
        ]}
        receivingTeamSees={[
          "The new Job with the full commercial and operational scope, ready for execution planning.",
          "Operations picks up job setup and execution from here — Finance's gate work on this order stops at handoff.",
        ]}
        onConfirm={createJob}
      />
    </>
  );
}

function GateVerdict({ decision, gateStatus, holds, advances, onException, onCreateJob, creatingJob }) {
  if (!decision) return (
    <Card title="Finance Gate verdict">
      <div className="text-sm text-slate-500">No decision yet — run the Finance Gate check above.</div>
    </Card>
  );
  const checks = [
    { ok: true, label: "Acceptance verified" },
    { ok: decision.creditDecision ? decision.creditDecision.withinLimit !== false : gateStatus !== "MANAGEMENT_EXCEPTION_REQUIRED", label: "Credit profile / exposure within limit" },
    { ok: !holds?.length, label: holds?.length ? `${holds.length} active Finance Hold(s)` : "No active Finance Holds" },
    { ok: !(decision.advanceDecision && gateStatus === "ADVANCE_REQUIRED"), label: decision.advanceDecision ? `Advance ${decision.advanceDecision.status === "VERIFIED" ? "verified" : "— " + decision.advanceDecision.requiredAmount + " required"}` : "No advance required" },
  ];
  const verdictStyle = {
    RELEASED: "border-emerald-200 bg-emerald-50 text-emerald-800",
    ADVANCE_REQUIRED: "border-amber-200 bg-amber-50 text-amber-800",
    ON_HOLD: "border-red-200 bg-red-50 text-red-800",
    MANAGEMENT_EXCEPTION_REQUIRED: "border-violet-200 bg-violet-50 text-violet-800",
    REVIEW_REQUIRED: "border-amber-200 bg-amber-50 text-amber-800",
    REJECTED: "border-red-200 bg-red-50 text-red-800",
  }[gateStatus] || "border-slate-200 bg-slate-50 text-slate-700";

  return (
    <Card title="Finance Gate verdict">
      <div className="space-y-1.5">
        {checks.map((c, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            {c.ok ? <Check className="h-4 w-4 text-emerald-600 shrink-0" /> : <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />}
            <span className={c.ok ? "text-slate-700" : "text-amber-800 font-medium"}>{c.label}</span>
          </div>
        ))}
      </div>
      <div className={`mt-3 rounded-lg border p-3 text-sm ${verdictStyle}`} data-testid="fg-verdict">
        <div className="font-semibold">SYSTEM RESULT: {gateStatus?.replace(/_/g, " ")}</div>
        <div className="mt-0.5">{decision.reason}</div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        {gateStatus === "RELEASED" && (
          // Aug 2026 IA correction: the action itself now lives once,
          // persistently, in the top-right "Create Job — Hand off to
          // Operations" button — no second trigger location on this page.
          <div className="text-[13px] text-emerald-700 font-medium" data-testid="fg-create-job-note">
            Use "Create Job — Hand off to Operations" at the top of this page to proceed.
          </div>
        )}
        {gateStatus === "MANAGEMENT_EXCEPTION_REQUIRED" && (
          <Button variant="outline" onClick={() => onException({
            decisionType: "FINANCE_CREDIT_EXCEPTION", objectType: "customer_acceptance",
            // The exception is only ever honored if requested for the EXACT
            // over-limit amount the gate computed (see finance_gate.py
            // has_valid_approval binding) — always source the amount from
            // the matching open hold, never let Finance type a guess here.
            defaultAmount: holds?.find((h) => h.reason === "CREDIT_LIMIT_EXCEEDED")?.amount,
            contextSummary: decision.reason,
          })} data-testid="fg-request-exception">
            <Gavel className="h-4 w-4 mr-1.5" />Request Management exception
          </Button>
        )}
      </div>
    </Card>
  );
}

function AdvanceRow({ adv, onCommunicate, onAttachProof, onVerify, onViewProof, onWaive }) {
  const blocked = ["REQUESTED", "PAYMENT_PROOF_RECEIVED", "RECEIVED_UNVERIFIED", "PARTIALLY_RECEIVED", "SHORTFALL"].includes(adv.status);
  return (
    <div id="advance-section" className="rounded-lg border border-slate-200 p-3.5" data-testid={`advance-${adv.id}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-semibold text-slate-800 text-sm">{adv.category === "DUTY" ? "Duty / VAT advance" : "Freight prepayment"}</div>
          <div className="text-[12px] text-slate-500">{adv.purpose}</div>
        </div>
        <StatusPill status={adv.status} />
      </div>
      <div className="grid grid-cols-3 gap-3 mt-2 text-sm">
        <Stat label="Required" value={money(adv.requestedAmount, adv.currency)} />
        <Stat label="Reported / Verified" value={adv.verifiedAmount != null ? money(adv.verifiedAmount, adv.currency) : (adv.amountReceived != null ? `${money(adv.amountReceived, adv.currency)} (unverified)` : "—")} />
        <Stat label="Remaining" value={adv.shortfall != null ? money(adv.shortfall, adv.currency) : (adv.status === "VERIFIED" ? money(0, adv.currency) : money(adv.requestedAmount, adv.currency))} danger={adv.shortfall > 0} />
      </div>
      {blocked && (
        <div className="mt-2 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] font-semibold text-red-700" data-testid={`advance-blocked-${adv.id}`}>
          <Lock className="h-3.5 w-3.5" /> ORDER REMAINS BLOCKED until this advance is fully verified
        </div>
      )}
      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onCommunicate} data-testid={`advance-request-${adv.id}`}><MessageCircle className="h-3.5 w-3.5 mr-1" />Request payment</Button>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onAttachProof} data-testid={`advance-proof-${adv.id}`}><Upload className="h-3.5 w-3.5 mr-1" />Record payment proof</Button>
        {adv.paymentProofDocumentId && <Button size="sm" variant="ghost" className="h-7 text-xs text-indigo-600" onClick={onViewProof} data-testid={`advance-view-proof-${adv.id}`}><FileCheck className="h-3.5 w-3.5 mr-1" />View proof</Button>}
        {["RECEIVED_UNVERIFIED", "PARTIALLY_RECEIVED", "SHORTFALL"].includes(adv.status) && (
          <Button size="sm" className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700" onClick={onVerify} data-testid={`advance-verify-${adv.id}`}><ShieldCheck className="h-3.5 w-3.5 mr-1" />Verify payment</Button>
        )}
        {adv.status === "SHORTFALL" && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onWaive} data-testid={`advance-waive-${adv.id}`}><Gavel className="h-3.5 w-3.5 mr-1" />Request exception (waive shortfall)</Button>
        )}
      </div>
      <div className="mt-1.5 text-[11px] text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" />
        {adv.status === "REQUESTED" && "Awaiting customer payment proof."}
        {adv.status === "PAYMENT_PROOF_RECEIVED" && "Customer says paid — proof recorded, not yet Finance-verified."}
        {adv.status === "RECEIVED_UNVERIFIED" && "Proof received — Awaiting Finance verification."}
        {adv.status === "VERIFIED" && `Verified by ${adv.verifiedBy || "Finance"} on ${adv.verifiedAt ? new Date(adv.verifiedAt).toLocaleString() : ""}.`}
        {adv.status === "SHORTFALL" && "Partial payment verified — shortfall remains."}
      </div>
    </div>
  );
}

function VerifyAdvanceDialog({ adv, onOpenChange, onDone }) {
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  if (!adv) return null;
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/finance-gate/advance-requests/${adv.id}/verify`, { verifiedAmount: Number(amount), expectedVersion: adv.version });
      toast.success("Advance verification recorded");
      onDone?.(); onOpenChange(false);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not verify")); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!adv} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="verify-advance-dialog">
        <DialogHeader><DialogTitle className="font-display">Verify advance receipt</DialogTitle></DialogHeader>
        <div className="text-[13px] text-slate-500">Required: {money(adv.requestedAmount, adv.currency)}. Enter the amount actually confirmed received (e.g. via bank statement) — a partial amount will be recorded as a shortfall, never treated as full.</div>
        <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Verified amount received</Label>
        <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="verify-amount-input" />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || amount === ""} className="bg-indigo-600 hover:bg-indigo-700" data-testid="verify-amount-submit">
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}Confirm verification
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AttachProofDialog({ adv, customerId, onOpenChange, onDone }) {
  const [amount, setAmount] = useState("");
  const [bankRef, setBankRef] = useState("");
  const [uploading, setUploading] = useState(false);
  // Item 1 — the file is staged (never uploaded on selection); the file
  // itself is only sent to the API when "Save proof" (Submit) is clicked,
  // together with the claimed amount / bank reference entered alongside it.
  const fileConfirm = useFileSelectConfirm(async () => {});
  if (!adv) return null;
  const submit = async () => {
    const file = fileConfirm.pending;
    if (!file) { toast.error("Attach the payment proof file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("jobId", "auto"); fd.append("docType", "Payment Proof"); fd.append("customerId", customerId || "");
      const { data: doc } = await api.post("/documents/upload", fd);
      await api.post(`/finance-gate/advance-requests/${adv.id}/payment-proof`, {
        documentId: doc.id, amountClaimed: amount ? Number(amount) : undefined, bankReference: bankRef || undefined,
        expectedVersion: adv.version,
      });
      toast.success("Payment proof recorded — Proof Received, awaiting Finance verification");
      onDone?.(); onOpenChange(false);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not record payment proof")); }
    finally { setUploading(false); }
  };
  const cancel = () => { fileConfirm.reset(); onOpenChange(false); };
  return (
    <Dialog open={!!adv} onOpenChange={(v) => { if (!v) fileConfirm.reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md" data-testid="attach-proof-dialog">
        <DialogHeader><DialogTitle className="font-display">Record payment proof</DialogTitle></DialogHeader>
        <div className="text-[12px] text-slate-500">This records that a proof was received — it does NOT verify payment. Finance must still verify separately.</div>
        <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Proof file (PDF / image)</Label>
        <input ref={fileConfirm.inputRef} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={fileConfirm.onFileChosen} data-testid="attach-proof-file" />
        {!fileConfirm.pending ? (
          <Button type="button" variant="outline" size="sm" className="h-8 w-fit" onClick={fileConfirm.openPicker} data-testid="attach-proof-choose">
            <Upload className="h-3.5 w-3.5 mr-1.5" />Choose file
          </Button>
        ) : (
          <FileSelectConfirmBar pending={fileConfirm.pending} busy={false} showSubmit={false} onCancel={fileConfirm.cancel} onReUpload={fileConfirm.reUpload} />
        )}
        <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mt-2">Amount customer claims paid</Label>
        <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="attach-proof-amount" />
        <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mt-2">Bank reference (optional)</Label>
        <Input value={bankRef} onChange={(e) => setBankRef(e.target.value)} data-testid="attach-proof-bankref" />
        <DialogFooter>
          <Button variant="ghost" onClick={cancel} data-testid="attach-proof-cancel">Cancel</Button>
          <Button onClick={submit} disabled={uploading || !fileConfirm.pending} className="bg-indigo-600 hover:bg-indigo-700" data-testid="attach-proof-submit">
            {uploading && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}Save proof
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Card = ({ title, action, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <div className="flex items-center justify-between mb-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{title}</div>
      {action}
    </div>
    {children}
  </div>
);
const Grid = ({ children }) => <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{children}</div>;
const Stat = ({ label, value, accent, danger }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
    <div className={`text-sm font-semibold font-mono-num mt-0.5 ${danger ? "text-red-600" : accent ? "text-indigo-600" : "text-slate-800"}`}>{value}</div>
  </div>
);
